import { useEffect, useRef, useState } from "react";
import { MessageSquareText, X, Send, Sparkles } from "lucide-react";
import { API_URL, waLink } from "../data/site";

const SUGGESTIONS = [
  "Como funciona o desconto na conta de luz?",
  "Quanto custa o plano de celular?",
  "O seguro aceita motorista de aplicativo?",
];

const getSessionId = () => {
  let sid = localStorage.getItem("igreen_chat_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("igreen_chat_sid", sid);
  }
  return sid;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);
  const sid = useRef(getSessionId());

  useEffect(() => {
    if (!open || loaded) return;
    fetch(`${API_URL}/chat/history?session_id=${sid.current}`)
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open, loaded]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API_URL}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message: content }),
      });
      if (!resp.ok || !resp.body) throw new Error("bad response");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") break;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Estou com uma instabilidade agora. Fale com a gente no WhatsApp: https://wa.me/55920039023",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const renderContent = (text) => {
    const urlRe = /(https?:\/\/[^\s)]+)/g;
    return text.split(urlRe).map((part, i) =>
      urlRe.test(part) ? (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-[#00E676] underline underline-offset-2 break-all">
          WhatsApp
        </a>
      ) : (
        part
      )
    );
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-widget-toggle"
        aria-label="Abrir assistente virtual"
        className="fixed bottom-6 left-6 z-50 inline-flex items-center gap-2 rounded-full bg-[#0B261E] border border-[#00E676]/40 text-[#00E676] font-bold text-sm px-5 py-3.5 shadow-[0_0_24px_rgba(0,230,118,0.3)] transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-[0_0_36px_rgba(0,230,118,0.5)]"
      >
        {open ? <X size={19} /> : <Sparkles size={18} />}
        <span className="hidden sm:inline">{open ? "Fechar" : "Dúvidas? Green IA"}</span>
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 left-4 right-4 sm:left-6 sm:right-auto sm:w-[400px] z-50 rounded-3xl border border-[#00E676]/25 bg-[#0B261E]/97 backdrop-blur-xl shadow-2xl overflow-hidden flex flex-col"
          style={{ maxHeight: "min(560px, calc(100dvh - 130px))" }}
        >
          <div className="flex items-center gap-3 px-5 py-4 bg-[#04120E] border-b border-white/10">
            <span className="grid place-items-center w-10 h-10 rounded-full bg-[#00E676]/15 border border-[#00E676]/30 text-[#00E676]">
              <MessageSquareText size={18} />
            </span>
            <div className="flex-1">
              <p className="font-heading font-bold text-white text-sm">Green · Assistente iGreen</p>
              <p className="text-[11px] text-emerald-100/50">IA treinada só com informações da iGreen Energy</p>
            </div>
            <span className="flex items-center gap-1.5 text-[10px] text-[#00E676]">
              <span className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" /> online
            </span>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[220px]" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-center pt-4">
                <p className="text-sm text-emerald-100/60 leading-relaxed">
                  Oi! Eu sou a Green, assistente do Grupo Conexão. Pergunte sobre desconto na conta de luz, celular, seguros ou como ser licenciado.
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`chat-suggestion-${SUGGESTIONS.indexOf(s)}`}
                      className="text-left text-xs text-[#00E676] border border-[#00E676]/25 rounded-full px-4 py-2.5 hover:bg-[#00E676]/10 transition-colors duration-300"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#00E676] text-[#04120E] font-medium rounded-br-md"
                      : "bg-[#04120E]/80 border border-white/10 text-emerald-100/85 rounded-bl-md"
                  }`}
                >
                  {m.content ? renderContent(m.content) : (
                    <span className="inline-flex gap-1 py-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  )}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="px-4 py-3 border-t border-white/10 bg-[#04120E]/60">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Pergunte sobre a iGreen…"
                data-testid="chat-input"
                maxLength={1000}
                className="flex-1 rounded-full border border-white/12 bg-[#04120E]/80 px-4 py-3 text-sm text-white placeholder:text-emerald-100/35 outline-none focus:border-[#00E676]/60 transition-colors duration-300"
              />
              <button
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Enviar mensagem"
                className="grid place-items-center w-12 h-12 rounded-full bg-[#00E676] hover:bg-[#00C853] disabled:opacity-50 text-[#04120E] transition-[background-color,opacity] duration-300 shrink-0"
              >
                <Send size={17} />
              </button>
            </div>
            <p className="mt-2 text-center text-[10px] text-emerald-100/35">
              A IA pode errar. Para contratar, fale com a gente no{" "}
              <a href={waLink()} target="_blank" rel="noopener noreferrer" className="text-[#00E676]">WhatsApp</a>.
            </p>
          </div>
        </div>
      )}
    </>
  );
};
